import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { authActions, themeActions } from "../../store";
import TopBar from "./TopBar";
import {
  NAV_ITEMS,
  MOBILE_NAV_ITEMS,
  BOTTOM_NAV_ITEMS,
  LANGUAGES,
  RIGHT_PANEL,
} from "../../config/app.config";
import {
  useDrawerNav,
  useSocialLinks,
} from "../../core/sidebar/hooks/useSidebarNav";
import "./Layout.css";

const Layout = () => {
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isRightBarOpen, setIsRightBarOpen] = React.useState(false);
  const isDarkMode = useSelector((x) => (x.theme?.mode ?? "dark") === "dark");
  const RightPanel = RIGHT_PANEL ?? null;

  // Derive active item from current path
  const activeMenuItem = React.useMemo(() => {
    const path = location.pathname;
    if (path === "/" || path === "/home") return "feed";
    return path.replace("/", "") || "feed";
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleTheme = () => {
    dispatch(themeActions.toggleTheme());
  };

  // Mobilde menü açıkken body scroll'u engelle
  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    setIsMobileMenuOpen(false); // Mobilde menüyü kapat
    // MSAL'dan logout yap (Azure AD ile giriş yapıldıysa)
    const aadToken = localStorage.getItem("aadToken");
    if (aadToken) {
      instance.logoutRedirect({ onRedirectNavigate: () => false });
    }
    // Redux state + localStorage temizle, redirect yap
    dispatch(authActions.performLogout());
  };

  const menuItems = NAV_ITEMS;
  const mobileOnlyItems = MOBILE_NAV_ITEMS;
  const bottomMenuItems = BOTTOM_NAV_ITEMS;

  // API-driven sidebar nav items
  const { data: drawerNavItems = [] } = useDrawerNav();
  const { data: socialLinks = [] } = useSocialLinks();

  const handleMenuClick = (itemId, path) => {
    setIsMobileMenuOpen(false); // Mobilde menüyü kapat
    navigate(path);
  };

  /** Converts mobile expo-router SCREEN_ROUTE to web path */
  const toWebPath = (screenRoute) => {
    if (!screenRoute) return "/";
    // Strip /(main)/ prefix and any leading slashes
    let path = screenRoute.replace(/^\/?\(main\)\/?/, "");
    // Known mismatches between mobile & web route names
    const ROUTE_MAP = { suggestions: "suggest" };
    if (ROUTE_MAP[path]) path = ROUTE_MAP[path];
    return `/${path}`;
  };

  const handleApiNavClick = (item) => {
    setIsMobileMenuOpen(false);
    if (item.iteM_TYPE === "link" && item.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    } else if (item.screeN_ROUTE) {
      navigate(toWebPath(item.screeN_ROUTE));
    }
  };

  const handleSocialClick = (link) => {
    if (link.url) {
      window.open(link.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="app-layout">
      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-backdrop" onClick={toggleMobileMenu}></div>
      )}

      {/* Top Bar — sticky, full width */}
      <TopBar onMobileMenuToggle={toggleMobileMenu} />

      <div className="layout-body">
        {/* Sol Sidebar */}
        <div className={`sidebar-col ${isMobileMenuOpen ? "mobile-open" : ""}`}>
          <div className="sidebar">
            <nav className="sidebar-menu">
              {/* Static nav items (Anasayfa) */}
              {menuItems.map((item) => (
                <div
                  key={item.id}
                  className={`menu-item ${activeMenuItem === item.id ? "active" : ""}`}
                  onClick={() => handleMenuClick(item.id, item.path)}
                >
                  <i className={item.icon}></i>
                  <span>{t(item.labelKey)}</span>
                </div>
              ))}

              {/* API-driven nav items */}
              {drawerNavItems
                .filter((item) => item.iteM_TYPE !== "social_link")
                .sort((a, b) => a.ordeR_INDEX - b.ordeR_INDEX)
                .map((item) => {
                  const webPath = item.screeN_ROUTE
                    ? toWebPath(item.screeN_ROUTE)
                    : null;
                  const isActive = webPath && location.pathname === webPath;
                  return (
                    <div
                      key={`api-${item.id}`}
                      className={`menu-item ${isActive ? "active" : ""}`}
                      onClick={() => handleApiNavClick(item)}
                    >
                      <i
                        className={`mdi mdi-${item.icoN_NAME}`}
                        style={{ fontSize: "1.15rem" }}
                      ></i>
                      <span>{item.title}</span>
                      {item.iteM_TYPE === "link" && (
                        <i
                          className="fas fa-external-link-alt"
                          style={{
                            marginLeft: "auto",
                            fontSize: "0.75rem",
                            opacity: 0.5,
                          }}
                        ></i>
                      )}
                    </div>
                  );
                })}

              {/* Mobilde görünen ekstra menüler */}
              <div className="mobile-only-divider mobile-only"></div>
              {mobileOnlyItems.map((item) => (
                <div
                  key={item.id}
                  className={`menu-item mobile-only ${activeMenuItem === item.id ? "active" : ""}`}
                  onClick={() => handleMenuClick(item.id, item.path)}
                >
                  <i className={item.icon}></i>
                  <span>{t(item.labelKey)}</span>
                </div>
              ))}
            </nav>

            {/* MyDTAS Button */}
            <div
              className={`menu-item mydtas-menu-item ${activeMenuItem === "mydtas" ? "active" : ""}`}
              onClick={() => handleMenuClick("mydtas", "/mydtas")}
            >
              <i className="fas fa-th-large"></i>
              <span>{t("myDTAS")}</span>
            </div>

            {/* Social Media Links */}
            {socialLinks.length > 0 && (
              <div className="sidebar-social-links">
                {socialLinks
                  .sort((a, b) => a.ordeR_INDEX - b.ordeR_INDEX)
                  .map((link) => (
                    <button
                      key={`social-${link.id}`}
                      className="sidebar-social-btn"
                      onClick={() => handleSocialClick(link)}
                      title={link.title}
                      aria-label={link.title}
                    >
                      <i className={`mdi mdi-${link.icoN_NAME}`}></i>
                    </button>
                  ))}
              </div>
            )}

            <div className="sidebar-bottom">
              {/* Theme Toggle */}
              <div className="theme-toggle-container">
                <span className="theme-label">
                  <i className={`fas ${isDarkMode ? "fa-moon" : "fa-sun"}`}></i>
                  <span style={{ marginLeft: "0.5rem" }}>
                    {isDarkMode ? t("nav.darkTheme") : t("nav.lightTheme")}
                  </span>
                </span>
                <button
                  className="theme-toggle-btn"
                  onClick={toggleTheme}
                  aria-label={t("nav.changeTheme")}
                >
                  <div
                    className={`toggle-slider ${isDarkMode ? "dark" : "light"}`}
                  ></div>
                </button>
              </div>

              {/* Language Switcher */}
              <div className="language-switcher">
                <span className="language-switcher-label">
                  <i className="fas fa-globe"></i>
                  <span style={{ marginLeft: "0.25rem" }}>
                    {t("nav.changeLanguage")}
                  </span>
                </span>
                <div className="lang-btn-group">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      className={`lang-btn ${i18n.language?.startsWith(lang.code) ? "active" : ""}`}
                      onClick={() => i18n.changeLanguage(lang.code)}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>

              {bottomMenuItems.map((item) => (
                <div
                  key={item.id}
                  className={`menu-item ${activeMenuItem === item.id ? "active" : ""}`}
                  onClick={() => handleMenuClick(item.id, item.path)}
                >
                  <i className={item.icon}></i>
                  <span>{t(item.labelKey)}</span>
                </div>
              ))}
              <div
                className="menu-item logout-menu-item"
                onClick={handleLogout}
              >
                <i className="fas fa-sign-out-alt"></i>
                <span>{t("nav.logout")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Orta Content Area */}
        <div className="content-col">
          <main className="content-area">
            <Outlet />
          </main>
        </div>

        {/* Sağ Panel — app.config'den gelir, yeni projede null */}
        {RightPanel && (
          <>
            {/* Desktop — always visible */}
            <div className="rightbar-col rightbar-desktop">
              <RightPanel />
            </div>

            {/* Mobile — slide-in drawer */}
            {isRightBarOpen && (
              <div
                className="rightbar-drawer-backdrop"
                onClick={() => setIsRightBarOpen(false)}
              />
            )}
            <div className={`rightbar-drawer ${isRightBarOpen ? "open" : ""}`}>
              <RightPanel />
            </div>

            {/* Toggle button — only visible on narrow screens */}
            <button
              className="rightbar-toggle-btn"
              onClick={() => setIsRightBarOpen((v) => !v)}
              aria-label={t("nav.toggleRightbar")}
            >
              <i
                className={`fas ${isRightBarOpen ? "fa-times" : "fa-columns"}`}
              ></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Layout;
